import { jsx } from 'react/jsx-runtime';
import { createContext, useEffect, useState, useContext } from 'react';
import { ThemeProvider } from 'styled-components';
export { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from './theme.js';
import '../hooks/index.js';
import { localStorageKeys } from '@sketch/constants';
import { getParsedItem } from '../utils.js';
import { useLocalStorage } from '../hooks/useLocalStorage.js';
import { useKonamiCode } from '../hooks/useKonamiCode.js';

const themes = {
    light: lightTheme,
    dark: darkTheme,
};
const context = createContext([
    {
        theme: 'light',
        matchSystem: false,
        enableLightMode: () => null,
        enableDarkMode: () => null,
        setMatchSystem: () => null,
    },
    // eslint-disable-next-line
    () => { },
]);
const ThemeManager = ({ children }) => {
    const [theme, setTheme] = useLocalStorage(localStorageKeys.theme, 'light');
    const [matchSystem, setMatchSystem] = useLocalStorage(localStorageKeys.matchSystemTheme, false);
    const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');
    useKonamiCode(toggleTheme);
    const enableLightMode = () => setTheme('light');
    const enableDarkMode = () => setTheme('dark');
    // listening to OS System Theme setting changes, to update our theme in case `matchSystem` is on
    useEffect(() => {
        const matchSystemAutomatically = (prefersDarkScheme) => {
            // using getParsedItem because it's the only way to get the most updated setting,
            // `matchSystem` would return a non-accurate value from time to time
            if (getParsedItem(localStorageKeys.matchSystemTheme)) {
                prefersDarkScheme ? setTheme('dark') : setTheme('light');
            }
        };
        window
            .matchMedia('(prefers-color-scheme: dark)')
            .addEventListener('change', ({ matches: prefersDarkScheme }) => matchSystemAutomatically(prefersDarkScheme));
        return window
            .matchMedia('(prefers-color-scheme: dark)')
            .removeEventListener('change', ({ matches: prefersDarkScheme }) => matchSystemAutomatically(prefersDarkScheme));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const value = useState({
        theme,
        matchSystem,
        enableLightMode,
        enableDarkMode,
        setMatchSystem,
    });
    return (jsx(context.Provider, Object.assign({ value: value }, { children: jsx(ThemeProvider, Object.assign({ theme: themes[theme] }, { children: children })) })));
};
function useThemeContext() {
    const [values, setValues] = useContext(context);
    const { theme, matchSystem, enableLightMode, enableDarkMode, setMatchSystem, } = values;
    // updates the state, so the radio buttons / selections are actually updated
    // and only afterwards calls the provider function, that syncs the local storage
    const updateDarkMode = () => {
        setValues(Object.assign(Object.assign({}, values), { theme: 'dark', matchSystem: false }));
        enableDarkMode();
        setMatchSystem(false);
    };
    const updateLightMode = () => {
        setValues(Object.assign(Object.assign({}, values), { theme: 'light', matchSystem: false }));
        enableLightMode();
        setMatchSystem(false);
    };
    return {
        theme,
        matchSystem,
        enableDarkMode: updateDarkMode,
        enableLightMode: updateLightMode,
        toggleMatchSystem: () => {
            const newMatchSystem = !matchSystem;
            setMatchSystem(newMatchSystem);
            let newTheme = theme;
            // checks for the system settings
            if (newMatchSystem && window.matchMedia) {
                if (window.matchMedia('(prefers-color-scheme: dark)').matches &&
                    theme !== 'dark') {
                    newTheme = 'dark';
                    enableDarkMode();
                }
                if (window.matchMedia('(prefers-color-scheme: light)').matches &&
                    theme !== 'light') {
                    newTheme = 'light';
                    enableLightMode();
                }
            }
            // updates the state, so the radio buttons / selections are actually updated
            setValues(Object.assign(Object.assign({}, values), { theme: newTheme, matchSystem: newMatchSystem }));
        },
    };
}

export { ThemeManager, useThemeContext };
